import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../data';
import { lookupOrder } from '../services/checkoutService';

function formatOrderStatus(status) {
  const labels = {
    'draft': 'Draft',
    'pending_submission': 'Submitting',
    'order_received': 'Order Received',
    'awaiting_payment_proof': 'Awaiting Payment Proof',
    'payment_review': 'Processing — Payment Under Review',
    'confirmed': 'Confirmed',
    'processing': 'Processing',
    'packed': 'Packed',
    'shipped': 'Shipped',
    'delivered': 'Delivered',
    'cancelled': 'Cancelled',
    'rejected': 'Rejected',
    'refunded': 'Refunded'
  };
  return labels[status] || status?.replaceAll('_', ' ') || 'Processing';
}

function formatPaymentStatus(status) {
  const labels = {
    'unpaid': 'Unpaid',
    'pending': 'Pending',
    'proof_submitted': 'Proof Submitted',
    'under_review': 'Under Review',
    'verified': 'Verified',
    'paid': 'Paid',
    'rejected': 'Rejected',
    'new_proof_requested': 'New Proof Requested'
  };
  return labels[status] || status?.replaceAll('_', ' ') || 'Pending';
}

function OrderTrackingPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [state, setState] = useState({
    loading: false,
    error: ''
  });

  const submit = async (event) => {
    event.preventDefault();
    setState({ loading: true, error: '' });
    setOrder(null);

    try {
      const result = await lookupOrder(orderNumber.trim());
      setOrder(result);
      setState({ loading: false, error: '' });
    } catch (error) {
      setState({
        loading: false,
        error: error.message
      });
    }
  };

  return (
    <main className="tracking-page">
      <header className="tracking-heading">
        <span className="eyebrow">Order Tracking</span>
        <h1>Follow Your ZEVI</h1>
        <p>
          Enter the order number from this browser to view its secure status.
        </p>
      </header>

      <form className="tracking-form" onSubmit={submit}>
        <label>
          Order number
          <input
            required
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            placeholder="ZEV-20260925-001001"
          />
        </label>
        <button
          className="gold-button"
          type="submit"
          disabled={state.loading}
        >
          {state.loading ? 'Verifying…' : 'Track order'}
        </button>
        {state.error && (
          <p className="form-error" role="alert">{state.error}</p>
        )}
      </form>

      {order && (
        <section className="tracking-result">
          <div className="tracking-result-heading">
            <div>
              <span>Order number</span>
              <h2>{order.order_number}</h2>
            </div>
            {order.grand_total > 0 && (
              <strong>{formatCurrency(order.grand_total)}</strong>
            )}
          </div>

          {order._lookup_unavailable && (
            <p className="tracking-processing-note">
              Your order is being processed. Full details will be available
              shortly. Please check back soon or contact ZEVI for updates.
            </p>
          )}

          {!order._lookup_unavailable && (
            <dl>
              <div>
                <dt>Order status</dt>
                <dd>{formatOrderStatus(order.fulfilment_stage === 'packed' ? 'packed' : order.order_status)}</dd>
              </div>
              <div>
                <dt>Payment status</dt>
                <dd>{order.payment_method === 'cash_on_delivery' && order.payment_status === 'unpaid'
                  ? 'Pending to be paid upon delivery'
                  : formatPaymentStatus(order.payment_status)}</dd>
              </div>
              {order.payment_method && (
                <div>
                  <dt>Payment method</dt>
                  <dd>{order.payment_method}</dd>
                </div>
              )}
              <div>
                <dt>Placed</dt>
                <dd>{new Date(order.created_at).toLocaleString()}</dd>
              </div>
            </dl>
          )}

          {order.items?.length > 0 && (
            <div className="tracking-products">
              {order.items.map((item) => (
                <article key={`${item.product_name}-${item.quantity}`}>
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={`${item.product_name} saree`}
                    />
                  )}
                  <div>
                    <h3>{item.product_name}</h3>
                    <p>Quantity {item.quantity}</p>
                  </div>
                  <strong>{formatCurrency(item.line_total)}</strong>
                </article>
              ))}
            </div>
          )}

          <Link className="outline-button" to="/shop">
            Continue shopping
          </Link>
        </section>
      )}
    </main>
  );
}

export default OrderTrackingPage;